import React, {
    useState,
    useRef,
    useEffect,
    ChangeEvent,
    ClipboardEvent,
    KeyboardEvent,
} from "react"
import LoadingButton from "../button/LoadingButton"
import { RotatingLines } from "react-loader-spinner"

interface ResetOTPProps {
    handleContinueClick: (e: any) => void
    handleSendAgainClick: (e: any) => void
    otpValues?: string
    setOtpValues?: React.Dispatch<React.SetStateAction<string>>
    loadershow?: boolean
}

function ResetOTP({
    handleContinueClick,
    handleSendAgainClick = (e: any) => {},
    otpValues,
    setOtpValues,
    loadershow,
}: ResetOTPProps) {
    const inputRefs = useRef<HTMLInputElement[]>(Array(6).fill(null))
    const [pasteArray, setPasteArray] = useState<any>([])
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)

    const [emptyIndex, setEmptyIndex] = useState({})

    useEffect(() => {
        setPasteArray(["", "", "", "", "", ""])
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (error) focusOnEmptyField()
        // eslint-disable-next-line
    }, [error])

    const handleInputChange = (
        index: number,
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const { value } = event.target

        setEmptyIndex({ ...emptyIndex, [index]: !value })

        if (isNaN(Number(value))) return
        setError("")
        const updatedOtp = [...pasteArray]
        updatedOtp[index] = value
        setPasteArray(updatedOtp)

        if (value && index <= 6) {
            inputRefs.current[index + 1]?.focus()
        }
        if (setOtpValues) {
            setOtpValues(updatedOtp.join(""))
        }
    }

    const handleInputPaste = (event: ClipboardEvent<HTMLInputElement>) => {
        const pastedData = event.clipboardData.getData("text")
        const otpArray = pastedData
            .slice(0, 6) // Limit to 6 characters
            .split("")
            ?.map((char) => (isNaN(Number(char)) ? "" : char))

        setPasteArray(otpArray)
        if (setOtpValues) {
            setOtpValues(otpArray.join(""))
        }

        inputRefs.current[inputRefs.current.length - 1]?.focus()
    }

    const handleInputKeyDown = (
        index: number,
        event: KeyboardEvent<HTMLInputElement>
    ) => {
        if (
            event.key === "Backspace" &&
            !event.currentTarget.value &&
            index > 0
        ) {
            event.preventDefault()
            inputRefs.current[index - 1]?.focus()
        }
    }

    const focusOnEmptyField = () => {
        for (let i = 0; i < inputRefs.current.length; i++) {
            if (!inputRefs.current[i].value) {
                inputRefs.current[i]?.focus()
                return
            }
        }
        inputRefs.current[inputRefs.current.length - 1]?.focus()
    }

    const PasteIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => {
        return (
            <svg
                version="1.1"
                id="Layer_1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 32"
                enable-background="new 0 0 32 32"
            >
                <path
                    fill="none"
                    stroke="#ffff"
                    stroke-width="2"
                    stroke-miterlimit="10"
                    d="M17,6c0-1.1-0.9-2-2-2s-2,0.9-2,2h-3v4h10V6H17z"
                />
                <polyline
                    fill="none"
                    stroke="#ffff"
                    stroke-width="2"
                    stroke-miterlimit="10"
                    points="10,6 6,6 6,27 14,27 "
                />
                <polyline
                    fill="none"
                    stroke="#ffff"
                    stroke-width="2"
                    stroke-miterlimit="10"
                    points="24,15 24,6 20,6 "
                />
                <rect
                    x="14"
                    y="15"
                    fill="none"
                    stroke="#ffff"
                    stroke-width="2"
                    stroke-miterlimit="10"
                    width="12"
                    height="14"
                />
            </svg>
        )
    }

    return (
        <div className="flex flex-col items-center w-full gap-4">
            <div className="flex flex-row justify-between gap-2 items-center">
                {Array.from({ length: 6 }, (_, index) => (
                    <input
                        key={index}
                        required={true}
                        type="text"
                        autoFocus={index === 0}
                        maxLength={1}
                        className={`border w-full h-full px-1 py-3 text-2xl text-center outline-none 
            bg-transparent rounded ${
                emptyIndex[index] ? "!border-red-500" : "!border-gray-600"
            }`}
                        ref={(el) => (inputRefs.current[index] = el!)}
                        value={pasteArray[index] || ""}
                        onChange={(e) => handleInputChange(index, e)}
                        onPaste={handleInputPaste}
                        onKeyDown={(e) => handleInputKeyDown(index, e)}
                    />
                ))}

                <div
                    className="w-44 h-8 cursor-pointer "
                    onClick={async () => {
                        try {
                            const text = await navigator.clipboard.readText()
                            if (text) {
                                handleInputPaste({
                                    clipboardData: { getData: () => text },
                                    preventDefault: () => {},
                                } as unknown as React.ClipboardEvent<HTMLInputElement>)
                            }
                        } catch (err) {
                            console.error("Failed to read clipboard: ", err)
                        }
                    }}
                >
                    <PasteIcon />
                </div>
            </div>

            <div>
                {error && (
                    <p className="text-red-600 text-sm font-medium">{error}</p>
                )}
            </div>
            <div className="flex gap-2 items-center justify-center text-sm font-normal ">
                <span>Didn't receive the code?</span>
                <p
                    className="text-label cursor-pointer"
                    onClick={handleSendAgainClick}
                >
                    <span className="text-buttonColor hover:font-bold">
                        {loadershow ? (
                            <div className="flex items-center">
                                <div className="mr-1.5">Sending</div>
                                <RotatingLines
                                    visible={true}
                                    width="20"
                                    strokeWidth="3"
                                    animationDuration="0.75"
                                    strokeColor="white"
                                />
                            </div>
                        ) : (
                            "Send again"
                        )}
                    </span>
                </p>
            </div>
            <LoadingButton
                className="w-full"
                loading={loading}
                loadingType="circular"
                buttonType="button"
                onClick={(e) => {
                    const code = pasteArray.join("")
                    if (code.length < 6) return setError("OTP is required...")

                    handleContinueClick({
                        e,
                        code,
                        setError,
                        setLoading,
                    })
                }}
            >
                Confirm
            </LoadingButton>
        </div>
    )
}

export default ResetOTP
